import { useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { rpcClient } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';
import type {
  AssignTaskInput,
  ConvertTaskToTypeInput,
  ExecuteTaskActionInput,
  TaskTimelineEntry,
  TerminalTransitionTaskInput,
} from '@erp/rpc';
import type { SystemEntity } from '@erp/shared-types';
import { invalidateTaskQueries } from './invalidateTaskQueries';

/** Timeline entries fetched per page of the task detail's unified timeline. */
const TIMELINE_PAGE_SIZE = 50;

/**
 * The full task record for the detail page (`tasks.get`, which also returns
 * `subtaskIds`). Polls while the task is live so status changes surface
 * without a manual refresh — same cadence as the inbox detail pane.
 */
export function useTaskById(id: string | null | undefined) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  return useQuery({
    // Share the inbox pane's key so mutations invalidating ['task-detail']
    // refresh this page too.
    queryKey: ['task-detail', id],
    queryFn: () => rpcClient.tasks.get({ id: id ?? '' }),
    enabled: canReadTasks && !!id,
    refetchInterval: (q) => {
      const t = q.state.data;
      return t &&
        !t.deletedAt &&
        (t.status === 'in_progress' || t.status === 'pending')
        ? 5000
        : false;
    },
  });
}

/**
 * Direct subtasks of a task, ordered oldest-first (creation order matches the
 * checklist reading order). Drives both the subtask tree and the derived
 * phase stepper.
 */
export function useSubtasks(parentTaskId: string | null | undefined) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const query = useQuery({
    queryKey: ['task-subtasks', parentTaskId],
    queryFn: () =>
      rpcClient.tasks.list({
        parentTaskId: parentTaskId ?? '',
        limit: 100,
        offset: 0,
        sortBy: 'createdAt',
        sortOrder: 'asc',
      }),
    enabled: canReadTasks && !!parentTaskId,
  });
  return {
    subtasks: query.data?.tasks ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Offset-paginated unified audit timeline (`tasks.getTimeline`) flattened
 * across pages, with a `loadMore` for the timeline's pagination affordance.
 */
export function useTaskTimeline(taskId: string | null | undefined) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const [view, setView] = useState<'summary' | 'events'>('summary');
  const query = useInfiniteQuery({
    queryKey: ['task-timeline', taskId, view],
    queryFn: ({ pageParam }) =>
      rpcClient.tasks.getTimeline({
        id: taskId ?? '',
        view,
        limit: TIMELINE_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.pagination.hasMore
        ? allPages.length * TIMELINE_PAGE_SIZE
        : undefined,
    enabled: canReadTasks && !!taskId,
    // Full-history counts are deliberate for audit pagination. Refresh on
    // demand or task mutations instead of polling every loaded page.
  });

  const entries = useMemo(() => {
    const byId = new Map<string, TaskTimelineEntry>();
    for (const page of query.data?.pages ?? []) {
      for (const entry of page.entries) byId.set(entry.id, entry);
    }
    return [...byId.values()];
  }, [query.data]);

  return {
    entries,
    view,
    setView,
    refresh: () => {
      if (!query.isFetching) void query.refetch();
    },
    taskIds: query.data?.pages[0]?.taskIds ?? [],
    total: query.data?.pages[0]?.pagination.total ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
  };
}

/**
 * Invoke a declared task action (`tasks.executeAction`). On success the
 * timeline gains a human-action entry and the task may transition, so both
 * the timeline and every task surface are refreshed.
 */
export function useExecuteTaskAction(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ExecuteTaskActionInput) =>
      rpcClient.tasks.executeAction(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

/**
 * Give an untyped task a type (+ anchor) via `tasks.convertToType`. The type
 * name, related-entity badge and any parent link all change, so the whole
 * detail subtree is refreshed.
 */
export function useConvertTaskToType(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConvertTaskToTypeInput) =>
      rpcClient.tasks.convertToType(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

/**
 * Open tasks anchored to one business entity — the candidate parents offered
 * when typing a legacy task (a stage can attach to an existing parent on the
 * same invoice/email, but the UI never synthesizes one).
 */
export function useOpenTasksForAnchor(
  anchor: { entityType: SystemEntity; entityId: string } | null,
) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const query = useQuery({
    queryKey: ['task-list', 'open-for-anchor', anchor],
    queryFn: () =>
      rpcClient.tasks.list({
        anchorEntityType: anchor?.entityType,
        anchorEntityId: anchor?.entityId,
        statuses: ['pending', 'in_progress', 'needs_input'],
        limit: 50,
        offset: 0,
        sortBy: 'createdAt',
        sortOrder: 'asc',
      }),
    enabled: canReadTasks && anchor !== null,
  });
  return { tasks: query.data?.tasks ?? [], isLoading: query.isLoading };
}

export function useSendTaskBackToAgent(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      rpcClient.tasks.sendBackToAgent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

/** Take a task away from its agent, optionally handing it to a person. */
export function useDetachTaskFromAgent(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; assigneeUserId?: string }) =>
      rpcClient.tasks.detachFromAgent(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      // Every agent board the task may have sat on.
      void queryClient.invalidateQueries({ queryKey: ['agent-tasks'] });
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useAssignTask(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AssignTaskInput) => rpcClient.tasks.assign(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useUnassignTask(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => rpcClient.tasks.unassign(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useCompleteTask(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TerminalTransitionTaskInput) =>
      rpcClient.tasks.complete(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useFailTask(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TerminalTransitionTaskInput) =>
      rpcClient.tasks.fail(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useCancelTask(taskId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TerminalTransitionTaskInput) =>
      rpcClient.tasks.cancel(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['task-timeline', taskId],
      });
      invalidateTaskQueries(queryClient);
    },
  });
}

export function useTaskOutcomeContract(taskId: string, enabled = true) {
  return useQuery({
    queryKey: ['task-outcome-contract', taskId],
    queryFn: () => rpcClient.tasks.getOutcomeContract({ id: taskId }),
    enabled,
    staleTime: 0,
  });
}
